import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { PlaylistThumb } from "@web/components/PlaylistThumb.tsx";
import { EmptyState } from "@web/components/EmptyState.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { useOpenDetail } from "@web/features/detail/useOpenDetail.ts";
import { api } from "@web/lib/api.ts";

const TrackPlaylistsResponse = z.object({
  playlists: z.array(
    z.object({
      kind: z.enum(["playlist", "local"]),
      id: z.string(),
      name: z.string().nullable(),
      image_url: z.string().nullable(),
    }),
  ),
});

/** Track sheet — Spotify + local playlists that include this track. */
export function TrackPlaylistsSection({ trackId }: { trackId: string }) {
  const playlists = useQuery({
    queryKey: ["tracks", trackId, "playlists"],
    queryFn: async () => {
      const body = TrackPlaylistsResponse.parse(
        await api(`/api/tracks/${encodeURIComponent(trackId)}/playlists`),
      );
      return body.playlists;
    },
  });
  const openDetail = useOpenDetail();

  if (playlists.isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (playlists.isError) {
    return (
      <EmptyState
        title="Couldn't load playlists"
        description={
          playlists.error instanceof Error ? playlists.error.message : "Unknown error"
        }
      />
    );
  }

  const list = playlists.data ?? [];
  if (list.length === 0) {
    return (
      <EmptyState
        title="Not in any playlist"
        description="This track isn’t on any of your Spotify or local playlists yet."
      />
    );
  }

  return (
    <ul className="divide-y divide-line">
      {list.map((pl) => {
        const detailType = pl.kind === "local" ? "local_playlist" : "playlist";
        const kindLabel = pl.kind === "local" ? "Local" : "Spotify";
        return (
          <li key={`${pl.kind}:${pl.id}`}>
            <button
              type="button"
              className="flex w-full items-center gap-3 px-1 py-2 text-left hover:bg-row-hover"
              onClick={() =>
                openDetail(
                  {
                    type: detailType,
                    id: pl.id,
                    label: pl.name ?? "Playlist",
                  },
                  { mode: "push" },
                )
              }
            >
              <PlaylistThumb coverUrl={pl.image_url} alt="" className="size-9" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">
                  {pl.name ?? "Untitled playlist"}
                </span>
                <span className="block truncate text-xs text-faint">{kindLabel}</span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
